
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import HeroSection from './components/HeroSection';
import HowItWorksSection from './components/HowItWorksSection';
import VerificationSection from './components/VerificationSection';
import QuoteValueSection from './components/QuoteValueSection';
import ProjectGallerySection from './components/ProjectGallerySection';
import TestimonialsSection from './components/TestimonialsSection';
import UrgencyIncentiveSection from './components/UrgencyIncentiveSection';
import ContactFormSection from './components/ContactFormSection';
import Footer from '../../components/feature/Footer';

export default function Home() {
  useEffect(() => {
    // LocalBusiness Schema.org JSON-LD - Optimized for Bath Remodel Connecticut
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#localbusiness`,
      "name": "Bath Contractor For You - Connecticut Bath Remodel Specialists",
      "alternateName": ["Connecticut Bath Remodel Contractors", "CT Bathroom Remodeling Network", "Bath Remodel Connecticut", "Connecticut Bathroom Remodel"],
      "description": "Connecticut's premier bath remodel contractor matching service. We connect homeowners with verified, licensed, and top-rated bathroom remodeling professionals throughout CT. Specializing in complete bath remodels, shower installations, and luxury bathroom renovations. Serving Hartford, New Haven, Bridgeport, Stamford, Waterbury, and all Connecticut cities.",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/519ba40ca4fe89039cbd6154721591df/f3df62ea6357ffd9f5c3ad1896919444.png",
        "width": "200",
        "height": "200"
      },
      "image": [
        "https://static.readdy.ai/image/519ba40ca4fe89039cbd6154721591df/f3df62ea6357ffd9f5c3ad1896919444.png"
      ],
      "telephone": "+1-800-BATHROOM",
      "email": "info@bathcontractorforyou.com",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "CT",
        "addressCountry": "US",
        "addressLocality": "Connecticut"
      },
      "areaServed": [
        {
          "@type": "State",
          "name": "Connecticut",
          "alternateName": "CT"
        },
        {
          "@type": "City",
          "name": "Hartford",
          "containedInPlace": "Connecticut"
        },
        {
          "@type": "City", 
          "name": "New Haven",
          "containedInPlace": "Connecticut"
        },
        {
          "@type": "City",
          "name": "Bridgeport", 
          "containedInPlace": "Connecticut"
        },
        {
          "@type": "City",
          "name": "Stamford",
          "containedInPlace": "Connecticut"
        },
        {
          "@type": "City",
          "name": "Waterbury",
          "containedInPlace": "Connecticut"
        }
      ],
      "serviceType": ["Bath Remodel Connecticut", "Bathroom Remodeling Contractor Matching", "Connecticut Bathroom Renovation Services", "Bath Renovation CT"],
      "priceRange": "$5,000 - $50,000+",
      "paymentAccepted": ["Cash", "Credit Card", "Financing", "Check"],
      "currenciesAccepted": "USD",
      "openingHours": "Mo-Su 00:00-23:59",
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Connecticut Bath Remodel Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Complete Bath Remodel Connecticut",
              "description": "Full bathroom remodeling services including design, demolition, plumbing, tiling, and fixture installation throughout Connecticut"
            }
          },
          {
            "@type": "Offer", 
            "itemOffered": {
              "@type": "Service",
              "name": "Luxury Bath Remodel CT",
              "description": "High-end bathroom remodeling with premium materials, custom designs, and luxury fixtures"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Small Bath Remodel Connecticut",
              "description": "Space-maximizing bathroom remodeling solutions for smaller bathrooms"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Master Bath Remodel CT", 
              "description": "Complete master bathroom remodeling with spa-like features and modern amenities"
            }
          }
        ]
      },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "247",
        "bestRating": "5",
        "worstRating": "1"
      },
      "review": [
        {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": "Sarah Johnson"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": "5"
          },
          "reviewBody": "Excellent bath remodel service! They connected me with a fantastic contractor who transformed my Connecticut bathroom beautifully. The remodeling process was smooth and professional."
        }
      ],
      "sameAs": [
        "https://www.facebook.com/bathcontractorforyou",
        "https://www.twitter.com/bathremodelct",
        "https://www.linkedin.com/company/bathcontractorforyou"
      ],
      "knowsAbout": [
        "Bath Remodel Connecticut",
        "Bathroom Remodeling CT", 
        "Connecticut Bath Renovation",
        "Contractor Matching",
        "Licensed Bath Contractors Connecticut"
      ]
    };

    // Service Schema - Optimized for Bath Remodel Searches
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#service`,
      "name": "Professional Bath Remodel Connecticut Contractor Matching Service",
      "description": "Connecticut's premier bath remodel contractor matching service connecting homeowners with verified, licensed, and top-rated bathroom remodeling professionals. We specialize in complete bath remodels, luxury bathroom renovations, shower installations, and custom bathroom designs throughout Connecticut. Get free quotes, design consultation, financing options, and project management support for all bath remodel needs.",
      "provider": {
        "@type": "Organization",
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#organization`,
        "name": "Bath Contractor For You"
      },
      "areaServed": [
        {
          "@type": "State",
          "name": "Connecticut",
          "alternateName": "CT"
        }
      ],
      "serviceType": "Bath Remodel Contractor Matching and Bathroom Remodeling Services",
      "category": ["Bath Remodel Connecticut", "Bathroom Remodeling CT", "Connecticut Bath Renovation", "Contractor Services"],
      "audience": {
        "@type": "Audience",
        "audienceType": "Connecticut Homeowners Seeking Bath Remodel"
      },
      "brand": {
        "@type": "Brand",
        "name": "Bath Contractor For You"
      },
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": "Free bath remodel contractor matching service with comprehensive quotes, design consultation, and financing options for Connecticut homeowners",
        "availability": "https://schema.org/InStock",
        "validFrom": "2024-01-01",
        "priceValidUntil": "2025-12-31"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Complete Bath Remodel Connecticut Services",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Full Bath Remodel Connecticut",
              "description": "Complete bathroom transformation including design, demolition, plumbing, electrical, tiling, and fixture installation throughout Connecticut"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Luxury Bath Remodel CT",
              "description": "High-end bathroom remodeling with premium materials, custom designs, spa features, and luxury fixtures"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Master Bath Remodel Connecticut",
              "description": "Complete master bathroom remodeling with walk-in showers, soaking tubs, double vanities, and modern amenities"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Small Bath Remodel CT",
              "description": "Space-maximizing bathroom remodeling solutions for smaller bathrooms with smart storage and efficient layouts"
            }
          }
        ]
      },
      "serviceOutput": [
        "Free Bath Remodel Quotes",
        "Custom Bathroom Design Concepts", 
        "Licensed Connecticut Contractor Matching",
        "Bath Remodel Timeline Planning",
        "Bathroom Financing Options"
      ]
    };

    // Organization Schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#organization`,
      "name": "Bath Contractor For You - Connecticut Bath Remodel Specialists",
      "alternateName": ["Connecticut Bath Remodel Contractors", "CT Bathroom Remodeling Network", "Bath Remodel Connecticut"],
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/519ba40ca4fe89039cbd6154721591df/f3df62ea6357ffd9f5c3ad1896919444.png",
        "width": "200",
        "height": "200"
      },
      "description": "Connecticut's most trusted bath remodel contractor network. We connect homeowners with pre-screened, licensed professionals specializing in bathroom remodeling throughout CT.",
      "slogan": "Connecticut's #1 Bath Remodel Contractor Network",
      "foundingDate": "2020",
      "numberOfEmployees": "25-50",
      "address": {
        "@type": "PostalAddress",
        "addressRegion": "CT",
        "addressCountry": "US",
        "addressLocality": "Connecticut"
      },
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "telephone": "+1-800-BATHROOM",
          "areaServed": "CT",
          "availableLanguage": ["English", "Spanish"],
          "hoursAvailable": "Mo-Su 00:00-23:59"
        },
        {
          "@type": "ContactPoint",
          "contactType": "sales",
          "areaServed": "CT",
          "availableLanguage": "English"
        }
      ],
      "sameAs": [
        "https://www.facebook.com/bathcontractorforyou",
        "https://www.twitter.com/bathremodelct",
        "https://www.linkedin.com/company/bathcontractorforyou",
        "https://www.instagram.com/bathremodelct"
      ],
      "knowsAbout": [
        "Bath Remodel Connecticut",
        "Bathroom Remodeling CT",
        "Connecticut Bath Renovation",
        "Contractor Licensing",
        "Connecticut Building Codes",
        "Project Management"
      ],
      "memberOf": {
        "@type": "Organization",
        "name": "Better Business Bureau"
      }
    };

    // WebSite Schema
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#website`,
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "name": "Bath Remodel Connecticut - Premier Bathroom Remodeling Contractors CT",
      "alternateName": "Connecticut Bath Remodel Contractors",
      "description": "Connecticut's most trusted bath remodel contractor network connecting homeowners with verified bathroom remodeling professionals for all renovation needs",
      "publisher": {
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#organization`
      },
      "potentialAction": [
        {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        }
      ],
      "mainEntity": {
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#localbusiness`
      }
    };

    // FAQ Schema - Optimized for Bath Remodel Connecticut
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#faq`,
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How much does a bath remodel cost in Connecticut?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Bath remodel costs in Connecticut typically range from $8,000 for basic updates to $50,000+ for luxury renovations. A complete bath remodel averages $15,000-$25,000. Factors include bathroom size, materials, fixtures, and labor costs. Our Connecticut contractors provide free detailed quotes to help you budget accurately for your bath remodel project."
          }
        },
        {
          "@type": "Question",
          "name": "Are your bath remodel contractors licensed in Connecticut?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, all bath remodel contractors in our Connecticut network are fully licensed, insured, and verified. We thoroughly screen each bathroom remodeling contractor for licensing, insurance, references, and quality of work before adding them to our network."
          }
        },
        {
          "@type": "Question",
          "name": "How long does a bath remodel take in Connecticut?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Most bath remodels in Connecticut take 2-4 weeks depending on the scope. Simple bathroom updates may take 1-2 weeks, while complete bath remodels with plumbing changes can take 3-6 weeks. Your Connecticut contractor will provide a detailed timeline with your bath remodel quote."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer financing for bath remodel projects in Connecticut?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, many of our Connecticut bath remodel contractors offer financing options including payment plans, low-interest loans, and special promotions. Bath remodel financing options will be discussed during your free consultation."
          }
        },
        {
          "@type": "Question",
          "name": "What cities in Connecticut do you serve for bath remodel projects?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We serve all Connecticut cities for bath remodel projects including Hartford, New Haven, Bridgeport, Stamford, Waterbury, Norwalk, Danbury, New Britain, West Hartford, Greenwich, Hamden, Meriden, Bristol, Manchester, West Haven, Milford, Stratford, East Hartford, Middletown, and all other CT communities."
          }
        }
      ]
    };

    // BreadcrumbList Schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#breadcrumb`,
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": import.meta.env.VITE_SITE_URL || "https://example.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Bath Remodel Connecticut",
          "item": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#bath-remodel-ct`
        }
      ]
    };

    // Create and append script tags
    const schemas = [localBusinessSchema, serviceSchema, organizationSchema, websiteSchema, faqSchema, breadcrumbSchema];
    const scriptTags: HTMLScriptElement[] = [];

    schemas.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      script.id = `schema-${index}`;
      document.head.appendChild(script);
      scriptTags.push(script);
    });

    // Cleanup function
    return () => {
      scriptTags.forEach(script => {
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      });
    };
  }, []);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Header />
      <HeroSection />
      <HowItWorksSection />
      <VerificationSection />
      <QuoteValueSection />
      <ProjectGallerySection />
      <TestimonialsSection />
      <UrgencyIncentiveSection />
      <ContactFormSection />
      <Footer />
    </div>
  );
}
